import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { countStudentBookings, getStudentChildrenFull } from "../../../services/dataService";
import { ageFrom, ageLabel, countLabel, sortChildren } from "../../../utils/studentDetail";
import { allShowingLabel, dateTimeLines } from "../../../utils/kitPresentation";
import { TableCard } from "../../../components/kit/SectionCard";
import Pager from "../../../components/kit/Pager";
import { EyeButton, PersonCell, TableMessageRow } from "../../../components/kit/TableParts";
import { errorText } from "./TabState";

const COLUMNS = ["Child", "Age", "Gender", "Sessions", "Join date", "Actions"];

// Figma's Children table (10739:55280) less what has no data yet (status, country, Add
// child, search and filters: the spec's Later list). One count per child (≤ 20, 044 D35).
// The list is read in full, so the foot says how many rows it has (047 D16).
const ChildrenTab = () => {
  const { student } = useOutletContext();
  const uid = student.id;
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = sortChildren(await getStudentChildrenFull(uid));
      setChildren(rows);
      rows.forEach((c) =>
        countStudentBookings(uid, { childId: c.id })
          .then((n) => setCounts((k) => ({ ...k, [c.id]: n })))
          .catch((e) => {
            console.error("child count failed:", e);
            setCounts((k) => ({ ...k, [c.id]: null }));
          })
      );
    } catch (e) {
      console.error("Error loading children:", e);
      setError(errorText(e, "Failed to load the children."));
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const settled = !loading && !error;
  return (
    <TableCard
      title="Children"
      subtitle={settled ? `${children.length} ${children.length === 1 ? "learner" : "learners"} under this parent` : ""}
      footer={<Pager label={settled ? allShowingLabel(children.length) : ""} />}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading || error || children.length === 0 ? (
              <TableMessageRow
                colSpan={COLUMNS.length}
                loading={loading}
                error={error}
                onRetry={load}
                emptyText="No children on this account — the student books for themself."
              />
            ) : (
              children.map((c) => (
                <TableRow key={c.id} hover onClick={() => navigate(`/students/${uid}/children/${c.id}`)} sx={{ cursor: "pointer" }}>
                  <TableCell>
                    <PersonCell name={c.name || "Child"} colorKey={c.id} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{ageLabel(ageFrom(c.date_of_birth)) || "-"}</TableCell>
                  <TableCell>{c.gender || "-"}</TableCell>
                  <TableCell>{c.id in counts ? countLabel(counts[c.id]) : "…"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{dateTimeLines(c.createdAt)?.date || "-"}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <EyeButton to={`/students/${uid}/children/${c.id}`} label="Open child" />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
};

export default ChildrenTab;
